import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Loader2, Delete, LogIn, ArrowLeft, ShieldAlert, WifiOff, RefreshCw } from 'lucide-react';
import { authAPI } from '../services/api';

// Écran de connexion : on choisit son prénom, on tape son code. Pensé pour un
// pouce sur un téléphone de chantier — grosses touches, pas de clavier système.
const LoginScreen = ({ onLogged }) => {
  const [comptes, setComptes] = useState(null);
  const [setup, setSetup] = useState(false);
  const [injoignable, setInjoignable] = useState(false);
  const [choisi, setChoisi] = useState(null);
  const [code, setCode] = useState('');
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);

  // Sans ce chargement, l'écran n'a aucun prénom à proposer. S'il échoue, il faut
  // le DIRE : un écran vide laisse croire que l'app est cassée, alors que c'est
  // le plus souvent le réseau privé qui s'est déconnecté (vécu sur téléphone).
  const charger = () => {
    setInjoignable(false);
    setComptes(null);
    authAPI.who()
      .then((data) => { setComptes(data.users || []); setSetup(!!data.setup); })
      .catch(() => { setComptes([]); setInjoignable(true); });
  };

  useEffect(charger, []);

  const taper = (chiffre) => {
    setErreur('');
    setCode((actuel) => (actuel.length >= 8 ? actuel : actuel + chiffre));
  };

  const valider = async () => {
    if (code.length < 4 || enCours) return;
    setEnCours(true);
    setErreur('');
    try {
      const { user } = await authAPI.login(choisi.id, code);
      onLogged(user);
    } catch (e) {
      setErreur(e.response?.data?.detail || "Connexion impossible — le serveur ne répond pas");
      setCode('');
    } finally {
      setEnCours(false);
    }
  };

  if (comptes === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50">
        <Loader2 className="w-10 h-10 text-green-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50 px-4 py-8">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-lg border border-green-200 p-6">
        <h1 className="text-xl font-bold text-green-800 text-center">Planification de Chantiers</h1>

        {injoignable && (
          <div className="mt-6 text-sm text-gray-700 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-2 font-semibold text-red-800 mb-2">
              <WifiOff className="w-4 h-4" /> Serveur injoignable
            </div>
            L'application est bien ouverte, mais elle n'arrive pas à joindre le serveur.
            Vérifiez votre <strong>connexion internet</strong>, puis réessayez.
            <Button onClick={charger} variant="outline" className="w-full mt-3">
              <RefreshCw className="w-4 h-4 mr-2" /> Réessayer
            </Button>
          </div>
        )}

        {!injoignable && setup && (
          <div className="mt-6 text-sm text-gray-700 bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-center gap-2 font-semibold text-amber-800 mb-2">
              <ShieldAlert className="w-4 h-4" /> Aucun compte pour l'instant
            </div>
            Sur le PC, dans le dossier <code>backend</code> :
            <div className="mt-2 font-mono text-xs bg-white border rounded p-2">
              python comptes.py creer "Samuel" patron
            </div>
          </div>
        )}

        {!injoignable && !setup && !choisi && (
          <>
            <p className="text-center text-gray-500 text-sm mt-1 mb-5">Qui êtes-vous ?</p>
            <div className="space-y-2">
              {comptes.map((compte) => (
                <button
                  key={compte.id}
                  onClick={() => { setChoisi(compte); setCode(''); setErreur(''); }}
                  className="w-full text-left px-4 py-4 rounded-xl border border-gray-200 hover:border-green-400
                             hover:bg-green-50 transition-colors text-lg font-medium text-gray-800"
                >
                  {compte.displayName}
                </button>
              ))}
            </div>
          </>
        )}

        {!injoignable && !setup && choisi && (
          <>
            <button
              onClick={() => { setChoisi(null); setCode(''); setErreur(''); }}
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 mt-4"
            >
              <ArrowLeft className="w-4 h-4" /> Ce n'est pas moi
            </button>
            <p className="text-center text-lg font-semibold text-gray-800 mt-3">{choisi.displayName}</p>
            <p className="text-center text-gray-500 text-sm mb-4">Votre code</p>

            <div className="flex justify-center gap-2 h-6 mb-4">
              {Array.from({ length: Math.max(code.length, 4) }).map((_, i) => (
                <span
                  key={i}
                  className={`w-3 h-3 rounded-full ${i < code.length ? 'bg-green-600' : 'bg-gray-200'}`}
                />
              ))}
            </div>

            {erreur && (
              <p className="text-center text-sm text-red-600 mb-3">{erreur}</p>
            )}

            <div className="grid grid-cols-3 gap-2">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((chiffre) => (
                <button
                  key={chiffre}
                  onClick={() => taper(String(chiffre))}
                  className="h-16 rounded-xl border border-gray-200 text-2xl font-semibold text-gray-800
                             hover:bg-green-50 active:bg-green-100"
                >
                  {chiffre}
                </button>
              ))}
              <button
                onClick={() => setCode('')}
                className="h-16 rounded-xl border border-gray-200 text-sm text-gray-500 hover:bg-gray-50"
              >
                Effacer
              </button>
              <button
                onClick={() => taper('0')}
                className="h-16 rounded-xl border border-gray-200 text-2xl font-semibold text-gray-800
                           hover:bg-green-50 active:bg-green-100"
              >
                0
              </button>
              <button
                onClick={() => setCode((c) => c.slice(0, -1))}
                className="h-16 rounded-xl border border-gray-200 flex items-center justify-center
                           text-gray-500 hover:bg-gray-50"
              >
                <Delete className="w-5 h-5" />
              </button>
            </div>

            <Button
              onClick={valider}
              disabled={code.length < 4 || enCours}
              className="w-full mt-4 h-12 text-base bg-green-600 hover:bg-green-700"
            >
              {enCours
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Connexion…</>
                : <><LogIn className="w-4 h-4 mr-2" /> Se connecter</>}
            </Button>

            {/* Un code ne se récupère pas : il n'est gardé que sous forme
                d'empreinte. Le dire ici évite d'essayer quinze fois et de
                bloquer le compte. */}
            <p className="text-center text-xs text-gray-400 mt-4">
              Code oublié, ou compte bloqué ? Demandez au responsable
              de vous en donner un nouveau.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default LoginScreen;
